import { Permission } from '@tp-vision/roles-permissions';
import { format } from 'date-fns';
import {
  ServiceAdminOrgAccessRequestFilter,
  ServiceAdminOrgAccessRequestStatus,
} from '~graphql/__generated__/types';

const AWAITING_REQUEST_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const INTERNAL_ORGANIZATION_IDS = (process.env.REACT_APP_INTERNAL_ORGANIZATION_IDS ?? '')
  .split(',')
  .map((id) => id.trim())
  .filter(Boolean);

export function isInternalOrganization(organizationId?: string) {
  return organizationId !== undefined && INTERNAL_ORGANIZATION_IDS.includes(organizationId);
}

export const accessRequestFilters = [
  ServiceAdminOrgAccessRequestFilter.All,
  ServiceAdminOrgAccessRequestFilter.Approved,
  ServiceAdminOrgAccessRequestFilter.Awaiting,
  ServiceAdminOrgAccessRequestFilter.Rejected,
  ServiceAdminOrgAccessRequestFilter.Revoked,
  ServiceAdminOrgAccessRequestFilter.Expired,
];

export interface AccessRequestTimes {
  status: ServiceAdminOrgAccessRequestStatus;
  createdAt: string;
  accessExpiresAt?: string | null;
}

export function getEffectiveStatus({
  status,
  createdAt,
  accessExpiresAt,
}: AccessRequestTimes): ServiceAdminOrgAccessRequestStatus {
  const now = Date.now();

  if (
    status === ServiceAdminOrgAccessRequestStatus.Awaiting &&
    now - new Date(createdAt).getTime() > AWAITING_REQUEST_TTL_MS
  ) {
    return ServiceAdminOrgAccessRequestStatus.Expired;
  }

  if (
    status === ServiceAdminOrgAccessRequestStatus.Approved &&
    accessExpiresAt &&
    new Date(accessExpiresAt).getTime() <= now
  ) {
    return ServiceAdminOrgAccessRequestStatus.Expired;
  }

  return status;
}

export function getExpiryDate(request: AccessRequestTimes): Date | null {
  switch (getEffectiveStatus(request)) {
    case ServiceAdminOrgAccessRequestStatus.Approved:
      return request.accessExpiresAt ? new Date(request.accessExpiresAt) : null;
    case ServiceAdminOrgAccessRequestStatus.Awaiting:
      return new Date(new Date(request.createdAt).getTime() + AWAITING_REQUEST_TTL_MS);
    default:
      return null;
  }
}

export function formatExpiryDate(request: AccessRequestTimes): string {
  const expiry = getExpiryDate(request);

  return expiry ? format(expiry, 'MMM d, y, HH:mm') : '-';
}

export function formatDurationMinutes(totalMinutes: number): string {
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  return [days ? `${days}d` : null, hours ? `${hours}h` : null, `${minutes}m`]
    .filter(Boolean)
    .join(' ');
}

export function canViewSentAccessRequests(
  verifyUserPermissions: (permissions: Permission[]) => boolean,
  isInternal: boolean,
) {
  return isInternal && verifyUserPermissions([Permission.ServiceAdminOrgAccessRequestView]);
}

export function canViewReceivedAccessRequests(
  verifyUserPermissions: (permissions: Permission[]) => boolean,
  isInternal: boolean,
) {
  return !isInternal && verifyUserPermissions([Permission.OrgAccessRequestView]);
}

export function canViewAccessRequests(
  verifyUserPermissions: (permissions: Permission[]) => boolean,
  isInternal: boolean,
) {
  return (
    verifyUserPermissions([Permission.FeatureStaff]) &&
    (canViewSentAccessRequests(verifyUserPermissions, isInternal) ||
      canViewReceivedAccessRequests(verifyUserPermissions, isInternal))
  );
}
